// src/TableEditor.jsx
import React, { useMemo, useState, useEffect } from "react";
import { API as DEFAULT_API } from "./apiBase";

function TableEditor({ tableName, initialData, onClose, tableApi: tableApiProp }) {
  // --- Per-table API base ---
  const tableApi =
    (typeof tableApiProp === "string" && tableApiProp) ||
    (typeof window !== "undefined" &&
      localStorage.getItem(`table_api:${tableName}`)) ||
    DEFAULT_API;

  // === Dedupe helpers ===
  const last8 = (v) => (v || "").toString().replace(/\D/g, "").slice(-8);
  const dedupeContacts = (list) => {
    const seen = new Set();
    return (list || []).filter((c) => {
      const key = last8(c.phones);
      if (!key) return true;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  };

  // ✅ Remove duplicates on load
  const [contacts, setContacts] = useState(() =>
    dedupeContacts(initialData.contacts)
  );
  const [metadata] = useState(initialData.metadata);
  const [busy, setBusy] = useState(false);

  // ===== Birthday helpers =====
  const today = useMemo(() => {
    const d = new Date();
    return new Date(d.getFullYear(), d.getMonth(), d.getDate());
  }, []);

  const parseBirthday = (raw) => {
    if (!raw) return null;
    const s = String(raw).trim();
    let m, d;
    const dash =
      s.match(/^(-{2})?(\d{2})-(\d{2})$|^(\d{4})-(\d{2})-(\d{2})$/) || null;
    if (dash) {
      if (dash[2] && dash[3]) {
        m = Number(dash[2]);
        d = Number(dash[3]);
      } else if (dash[4] && dash[5] && dash[6]) {
        m = Number(dash[5]);
        d = Number(dash[6]);
      }
    }
    if (!m && !d) {
      const slash =
        s.match(/^(\d{1,2})\/(\d{1,2})(?:\/\d{2,4})?$/) || null;
      if (slash) {
        d = Number(slash[1]);
        m = Number(slash[2]);
      }
    }
    if (!m || !d || m < 1 || m > 12 || d < 1 || d > 31) return null;
    return { month: m, day: d };
  };

  const daysUntilNext = (mm, dd) => {
    const y = today.getFullYear();
    let next = new Date(y, mm - 1, dd);
    if (next < today) next = new Date(y + 1, mm - 1, dd);
    return Math.round((next - today) / 86400000);
  };

  const daysSinceLast = (mm, dd) => {
    const y = today.getFullYear();
    let last = new Date(y, mm - 1, dd);
    if (last > today) last = new Date(y - 1, mm - 1, dd);
    return Math.round((today - last) / 86400000);
  };

  const getBdayInfo = (birthday) => {
    const b = parseBirthday(birthday);
    if (!b) return null;
    const until = daysUntilNext(b.month, b.day);
    const since = daysSinceLast(b.month, b.day);
    return {
      pretty: `${String(b.month).padStart(2, "0")}-${String(b.day).padStart(
        2,
        "0"
      )}`,
      inNextWeek: until >= 0 && until <= 7,
      inLastMonth: since > 0 && since <= 30,
      until,
      since,
    };
  };

  // ===== Handlers =====
  const handleChange = (index, field, value) => {
    const updated = [...contacts];
    updated[index][field] = value;
    setContacts(dedupeContacts(updated)); // ✅ dedupe after each change
  };

  const handleRemove = (index) => {
    const updated = [...contacts];
    updated.splice(index, 1);
    setContacts(updated);
  };

  const handleAddBlank = () => {
    setContacts((prev) =>
      dedupeContacts([
        ...prev,
        {
          name: "",
          phones: "",
          email: "",
          company: "",
          title: "",
          birthday: "",
          address: "",
          added_at: new Date().toISOString(),
        },
      ])
    );
  };

  const handleSave = async () => {
    try {
      setBusy(true);
      const uniqueContacts = dedupeContacts(contacts);
      const payload = {
        table_name: tableName,
        metadata,
        contacts: uniqueContacts,
      };

      const res = await fetch(`${tableApi}/save-table`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (res.ok) {
        alert(`✅ Table "${tableName}" saved!`);
        onClose();
      } else {
        const data = await res.json().catch(() => ({}));
        alert(`❌ Save failed: ${data?.error || res.status}`);
      }
    } catch (err) {
      console.error("Error saving edits:", err);
      alert("❌ Save failed");
    } finally {
      setBusy(false);
    }
  };

  // ---------- Enqueue to central queue ----------
  async function enqueueSend(items) {
    const res = await fetch(`${tableApi}/queue/enqueue`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ items }),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok || data?.ok !== true) {
      throw new Error(data?.detail || data?.error || `HTTP ${res.status}`);
    }
    return data;
  }

  const sendMenu = async () => {
    const info = contacts.reduce(
      (acc, c) => {
        const b = getBdayInfo(c.birthday);
        if (b?.inNextWeek) acc.upcoming.push(c.name || c.phones);
        if (b?.inLastMonth) acc.recent.push(c.name || c.phones);
        return acc;
      },
      { upcoming: [], recent: [] }
    );

    const parts = [];
    if (info.upcoming.length) parts.push(`🎉 Next 7 days: ${info.upcoming.length}`);
    if (info.recent.length) parts.push(`🎂 Last 30 days: ${info.recent.length}`);
    const summary = parts.length
      ? `\n\nBirthday notice:\n${parts.join("\n")}`
      : "";

    if (
      !window.confirm(
        `Enqueue menu send for all contacts in ${tableName}?${summary}`
      )
    )
      return;

    try {
      setBusy(true);
      const uniqueContacts = dedupeContacts(contacts);

      const items = uniqueContacts
        .map((row) => ({
          name: row.name || "",
          phone: (row.phones || row.phone || "").toString(),
          table_name: tableName,
          pdf_path: row.pdfPath || "",
        }))
        .filter((x) => x.phone);

      if (!items.length) {
        alert("No valid phone numbers to enqueue.");
        return;
      }

      const out = await enqueueSend(items);
      alert(
        `📤 Queued ${out.enqueued} contact(s) for sending.\n\nA background worker on the sender PC will process them.`
      );
    } catch (e) {
      console.error(e);
      alert(`❌ Failed to enqueue: ${e?.message || e}`);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="table-editor-root"
      style={{
        display: "flex",
        flexDirection: "column",
        maxHeight: "100%",
        minHeight: 0,
        gap: 8,
      }}
    >
      {/* Header */}
      <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            justifyContent: "space-between",
          }}
        >
          <h2 style={{ margin: 0, fontSize: "1rem" }}>
            📦 Editing Table: {tableName}
          </h2>
          <button className="btn ghost" type="button" onClick={onClose}>
            ✖ Close
          </button>
        </div>
        <p style={{ margin: "2px 0 4px", fontSize: "0.82rem", opacity: 0.9 }}>
          <b>Hostess:</b> {metadata.hostess} &nbsp;|&nbsp; <b>Waiter:</b>{" "}
          {metadata.waiter} &nbsp;|&nbsp; <b>Table #:</b> {metadata.table_number}
          &nbsp;|&nbsp; <b>Server:</b> {tableApi}
        </p>

        {/* Live queue status */}
        <QueueStatus api={tableApi} tableName={tableName} />

        <div
          style={{
            marginTop: 4,
            marginBottom: 4,
            display: "flex",
            gap: 8,
            flexWrap: "wrap",
          }}
        >
          <button className="btn" onClick={handleAddBlank}>
            ➕ Add Blank Contact
          </button>
          <button className="btn primary" onClick={sendMenu}>
            📤 Send Menu (enqueue)
          </button>
        </div>
      </div>

      {/* Scrollable table area */}
      <div
        style={{
          flex: 1,
          minHeight: 0,
          overflow: "auto",
          borderRadius: 10,
          border: "1px solid rgba(148,163,184,0.35)",
          background: "rgba(15,23,42,0.92)",
        }}
      >
        <table className="contact-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Phones</th>
              <th>Email</th>
              <th>Company</th>
              <th>Birthday</th>
              <th>Badges</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {contacts.map((c, i) => {
              const b = getBdayInfo(c.birthday);
              const rowBg = b?.inNextWeek
                ? "rgba(36,59,83,0.28)"
                : "transparent";
              return (
                <tr key={i} style={{ background: rowBg }}>
                  <td>
                    <input
                      value={c.name}
                      onChange={(e) =>
                        handleChange(i, "name", e.target.value)
                      }
                    />
                  </td>
                  <td>
                    <input
                      value={c.phones}
                      onChange={(e) =>
                        handleChange(i, "phones", e.target.value)
                      }
                    />
                  </td>
                  <td>
                    <input
                      value={c.email}
                      onChange={(e) =>
                        handleChange(i, "email", e.target.value)
                      }
                    />
                  </td>
                  <td>
                    <input
                      value={c.company}
                      onChange={(e) =>
                        handleChange(i, "company", e.target.value)
                      }
                    />
                  </td>
                  <td>
                    <input
                      value={c.birthday}
                      onChange={(e) =>
                        handleChange(i, "birthday", e.target.value)
                      }
                    />
                  </td>
                  <td>
                    <div
                      style={{
                        display: "flex",
                        gap: 6,
                        flexWrap: "wrap",
                        alignItems: "center",
                      }}
                    >
                      {c.birthday ? (
                        <small>🎂 {b?.pretty || c.birthday}</small>
                      ) : (
                        <small>🎂 —</small>
                      )}
                      {b?.inNextWeek && (
                        <span style={badgeStyle}>🎉 Next 7 days</span>
                      )}
                      {b?.inLastMonth && (
                        <span style={badgeStyleAlt}>🎂 Last month</span>
                      )}
                    </div>
                  </td>
                  <td>
                    <button className="btn ghost" onClick={() => handleRemove(i)}>
                      ❌ Remove
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Footer buttons */}
      <div
        style={{
          marginTop: 6,
          display: "flex",
          justifyContent: "space-between",
          gap: 8,
          flexWrap: "wrap",
        }}
      >
        <button
          className="btn primary"
          onClick={handleSave}
          disabled={busy}
          type="button"
        >
          💾 Save Changes
        </button>
        <button className="btn ghost" onClick={onClose} type="button">
          🔙 Back
        </button>
      </div>

      {busy && (
        <div className="spinner-overlay">
          <div className="spinner" />
          <p style={{ color: "white", marginTop: "10px" }}>Working...</p>
        </div>
      )}
    </div>
  );
}

/* ---------- Live Queue widget ---------- */
function QueueStatus({ api, tableName }) {
  const [stats, setStats] = useState({
    PENDING: 0,
    IN_PROGRESS: 0,
    SENT: 0,
    FAILED: 0,
  });
  const [rows, setRows] = useState([]);

  useEffect(() => {
    let stop = false;
    async function tick() {
      try {
        const s = await fetch(`${api}/queue/stats`).then((r) => r.json());
        if (!stop) setStats(s || {});
        const list = await fetch(
          `${api}/queue/pending?limit=200`
        ).then((r) => r.json());
        let items = list?.items || [];
        if (tableName)
          items = items.filter((i) => (i.table_name || "") === tableName);
        if (!stop) setRows(items);
      } catch {
        // ignore polling errors
      } finally {
        if (!stop) setTimeout(tick, 1500);
      }
    }
    tick();
    return () => {
      stop = true;
    };
  }, [api, tableName]);

  return (
    <div
      style={{
        padding: 8,
        border: "1px solid #333",
        borderRadius: 10,
        margin: "4px 0 8px",
        background: "rgba(15,23,42,0.9)",
      }}
    >
      <div style={{ fontWeight: 600, marginBottom: 6 }}>Queue</div>
      <div
        style={{ display: "flex", gap: 12, fontSize: 14, flexWrap: "wrap" }}
      >
        <span>🟡 PENDING: {stats.PENDING || 0}</span>
        <span>🔵 IN PROGRESS: {stats.IN_PROGRESS || 0}</span>
        <span>✅ SENT: {stats.SENT || 0}</span>
        <span>❌ FAILED: {stats.FAILED || 0}</span>
      </div>
      <div
        style={{
          maxHeight: 180,
          overflow: "auto",
          marginTop: 8,
          fontSize: 13,
        }}
      >
        {rows.length === 0 ? (
          <em style={{ opacity: 0.7 }}>No queued items for this table.</em>
        ) : (
          rows.map((r) => (
            <div
              key={r.id}
              style={{ display: "flex", gap: 8, alignItems: "baseline" }}
            >
              <code>#{r.id}</code>
              <span>{r.phone}</span>
              <em style={{ opacity: 0.7 }}>{r.status}</em>
              {r.last_error ? (
                <span style={{ color: "#e66" }}>{r.last_error}</span>
              ) : null}
            </div>
          ))
        )}
      </div>
    </div>
  );
}

/* ---------- Styles ---------- */
const badgeStyle = {
  fontSize: 11,
  padding: "2px 8px",
  borderRadius: 999,
  background: "#243b53",
  border: "1px solid #335c81",
  display: "inline-block",
};

const badgeStyleAlt = {
  fontSize: 11,
  padding: "2px 8px",
  borderRadius: 999,
  background: "#2a3b22",
  border: "1px solid #3c5a2f",
  display: "inline-block",
};

export default TableEditor;
